import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ArrowRight, Store, BadgePercent } from 'lucide-react';
import { Button } from '../ui/button';
import { lineQty, lineTotal, tierUnitPrice } from '../../lib/marketCart';

const rm = (v) => `RM ${Number(v || 0).toFixed(2)}`;

const Line = ({ i, k, onQty, onRemove }) => {
  const q = lineQty(i);
  const unit = i.tiered ? tierUnitPrice(i.price_tiers, i.sachets) : Number(i.price_rm || 0);
  const fixed = i.kind === 'recording';
  return (
    <li className="rounded-lg border border-slate-200 bg-white p-3 flex gap-3" data-testid={`market-drawer-line-${i.variant_sku || i.id}`}>
      <div className="h-14 w-14 shrink-0 rounded-md bg-[#FDF2F8] overflow-hidden flex items-center justify-center">
        {i.photo ? <img src={i.photo} alt="" className={`h-full w-full ${i.variant_sku ? 'object-contain bg-white' : 'object-cover'}`} /> : <Store size={16} className="text-[#B79CF0]" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-black text-[#101B3A] leading-snug truncate" data-testid={`market-drawer-name-${i.variant_sku || i.id}`}>{i.name}</p>
        {i.variant_label && <p className="text-[11px] font-bold text-slate-700 truncate" data-testid={`market-drawer-variant-${i.variant_sku}`}>{i.variant_label}{i.sku ? <span className="font-normal text-slate-400"> · {i.sku}</span> : null}</p>}
        <p className="text-[11px] text-slate-500">{rm(unit)} {i.tiered ? 'per sachet' : 'each'}</p>
        <div className="flex items-center justify-between mt-1.5">
          {fixed ? <span className="text-[11px] text-slate-500">Qty 1</span> : (
            <div className="inline-flex items-center border border-slate-200 rounded-md">
              <button type="button" onClick={() => onQty(k, q - 1)} className="h-7 w-7 flex items-center justify-center hover:bg-slate-100" aria-label="Decrease" data-testid={`market-drawer-dec-${i.variant_sku || i.id}`}><Minus size={12} /></button>
              <span className="w-8 text-center text-xs font-black" data-testid={`market-drawer-qty-${i.variant_sku || i.id}`}>{q}</span>
              <button type="button" onClick={() => onQty(k, q + 1)} className="h-7 w-7 flex items-center justify-center hover:bg-slate-100" aria-label="Increase" data-testid={`market-drawer-inc-${i.variant_sku || i.id}`}><Plus size={12} /></button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-sm font-black text-[#3F2774]" data-testid={`market-drawer-total-${i.variant_sku || i.id}`}>{rm(lineTotal(i))}</span>
            <button type="button" onClick={() => onRemove(k)} className="text-slate-400 hover:text-red-600" aria-label="Remove" data-testid={`market-drawer-remove-${i.variant_sku || i.id}`}><Trash2 size={13} /></button>
          </div>
        </div>
      </div>
    </li>
  );
};

/** Runners' Market lines inside the header cart drawer. Same store as the product pages
 *  and /runner-market/cart; final prices + stock are re-validated server-side at checkout. */
export const MarketDrawerLines = ({ market, onClose }) => {
  const navigate = useNavigate();
  if (!market.count) return null;
  return (
    <section className="mb-4" data-testid="market-drawer-section">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-[10px] font-black uppercase tracking-widest text-[#7E22CE] flex items-center gap-1.5"><Store size={12} /> Runners' Market · {market.count} item{market.count === 1 ? '' : 's'}</h3>
      </div>
      <ul className="space-y-2">
        {market.items.map((i) => <Line key={market.itemKey(i)} i={i} k={market.itemKey(i)} onQty={market.setQty} onRemove={market.remove} />)}
      </ul>
      <div className="rounded-lg bg-[#FAFAFA] border border-slate-200 p-3 mt-2 space-y-1 text-xs">
        <div className="flex justify-between text-slate-600"><span>Subtotal</span><span className="font-black text-[#101B3A]" data-testid="market-drawer-subtotal">{rm(market.subtotal)}</span></div>
        {market.discounts.map((d) => (
          <div key={d.series_key} className="flex justify-between text-emerald-800" data-testid={`market-drawer-discount-${d.series_key}`}>
            <span className="flex items-center gap-1"><BadgePercent size={11} /> {d.label} ({d.percent}%)</span><span className="font-black">− {rm(d.amount_rm)}</span>
          </div>
        ))}
        <div className="flex justify-between border-t border-slate-200 pt-1 text-sm"><span className="font-black text-[#101B3A]">Market total</span><span className="font-black text-[#7E22CE]" data-testid="market-drawer-total">{rm(market.total)}</span></div>
        <p className="text-[10px] text-slate-500">Delivery, wallet credit and member pricing are confirmed at checkout.</p>
      </div>
      <Button onClick={() => { onClose(); navigate('/runner-market/cart'); }} className="w-full mt-2 bg-[#7E22CE] hover:bg-[#6B21A8] text-xs font-black uppercase tracking-widest" data-testid="market-drawer-checkout">
        Review & checkout market items <ArrowRight size={12} className="ml-1" />
      </Button>
    </section>
  );
};
